// A „töltsd le a mobilappot" ajánló döntési logikája.
//
// A modul SZÁNDÉKOSAN nem ismeri a DOM-ot (a böngésző-adatokat paraméterként
// kapja), így böngésző nélkül is mérhető: `npm run verify:app-banner`.
//
// ALVÓ ÁLLAPOT. Ha egyik áruház-cím sincs kitöltve, a banner SOHA nem jelenik
// meg: olyan appot ajánlani, amit nem lehet letölteni, rosszabb, mint
// semmit ajánlani.

/// Az áruház-címek. Mindkettő élő (ellenőrizve: Google Play 200, iTunes
/// lookup `resultCount: 1`, bundle: com.blaze.app.blaze).
export const ANDROID_URL =
  'https://play.google.com/store/apps/details?id=com.blaze.app.blaze&hl=hu';
export const IOS_URL = 'https://apps.apple.com/hu/app/blaze/id6744900184';

export type Platform = 'android' | 'ios';

const KULCS = 'blaze.appBanner';

/// Ennyi ideig nem kérdezzük újra, ha a látogató elhessegette (14 nap).
export const ELREJTES_MS = 14 * 24 * 60 * 60 * 1000;

/// Melyik telefonos platformról van szó. `null`: asztali gép (vagy ismeretlen).
export function detectPlatform(ua: string, maxTouchPoints = 0, platform = ''): Platform | null {
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  // Az iPadOS 13+ Safari asztali (Macintosh) UA-t küld; érintőpontokról ismerjük fel.
  if (/Macintosh/i.test(ua) && platform === 'MacIntel' && maxTouchPoints > 1) return 'ios';
  return null;
}

/// Az app saját WebView-jában vagyunk-e. Ott az ajánló értelmetlen: a
/// látogató már az appban van.
export function isInAppWebView(ua: string, platform: Platform | null): boolean {
  // Android WebView: „; wv)" jelölés a user agentben.
  if (platform === 'android') return /;\s*wv\)/i.test(ua);
  // iOS WebView (WKWebView): nincs „Safari/" token (a Safari és a Chrome
  // iOS-en is küldi).
  if (platform === 'ios') return !/Safari\//i.test(ua);
  return false;
}

/// Az áruház-cím az adott platformra; üres, ha nincs kitöltve.
export function storeUrl(platform: Platform, urls = { android: ANDROID_URL, ios: IOS_URL }): string {
  return platform === 'android' ? urls.android : urls.ios;
}

/// Elrejtette-e a látogató az utóbbi 14 napban. Elérhetetlen tárolónál: nem.
export function isDismissed(now: number, storage: Pick<Storage, 'getItem'> | null): boolean {
  try {
    const nyers = storage?.getItem(KULCS);
    if (!nyers) return false;
    const ido = Number(nyers);
    return Number.isFinite(ido) && now - ido < ELREJTES_MS && now >= ido;
  } catch {
    return false;
  }
}

export function rememberDismissal(now: number, storage: Pick<Storage, 'setItem'> | null): void {
  try {
    storage?.setItem(KULCS, String(now));
  } catch {
    /* privát mód / tele tároló — a banner legfeljebb a következő betöltésnél újra megjelenik */
  }
}

/// Melyik platform áruház-címét mutassuk? `null`: ne jelenjen meg banner.
export function bannerTarget(input: {
  ua: string;
  maxTouchPoints?: number;
  platform?: string;
  now: number;
  storage: (Pick<Storage, 'getItem'> & Pick<Storage, 'setItem'>) | null;
  urls?: { android: string; ios: string };
}): { platform: Platform; url: string } | null {
  const p = detectPlatform(input.ua, input.maxTouchPoints, input.platform);
  if (!p) return null;
  if (isInAppWebView(input.ua, p)) return null;
  const url = storeUrl(p, input.urls);
  if (!url) return null;
  if (isDismissed(input.now, input.storage)) return null;
  return { platform: p, url };
}
